import { callFirebaseFunction } from 'boot/firebase';
import { documentId } from 'firebase/firestore';
import ClientsDB from 'src/fb/clients-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import UsersDB from 'src/fb/users-db';

import { toPolicyholderDemographics } from '../mappers/policyholderMapper';

/**
 * Reads and writes of people, for the coverage editor.
 *
 * THE READS ARE THIS CONTEXT'S, THE WRITE IS NOT. Reading a person document and projecting it is
 * something any consumer may do for itself. Creating one is not: that goes through the Person
 * context's published callable, which owns the writable field allowlist and the seat discriminator
 * that keeps a policyholder off the customer's invoice. This module never writes the collection.
 *
 * WHY THE WIDER SET IS FETCHED WHOLE. The picker matches on any part of a name, because somebody
 * about to add a second David Andersen has to see the first, and a prefix match would hide him.
 * Firestore does prefix ranges and nothing looser, so a per-keystroke query cannot express it. At
 * pilot scale the clinic's people are a small set, so it is read once and filtered in memory. A
 * clinic large enough for that to hurt needs a search index, which is a later concern.
 *
 * `type` IS A STORED DISCRIMINATOR, NOT A DOMAIN CONCEPT, so it appears in the query and nowhere
 * else. It answers a licensing question: which seat category a record consumes. Everyone who is not
 * a client seat is a candidate here, which is deliberate breadth. A therapist whose own child is a
 * client is a legitimate policyholder, and filtering by enrollment would exclude every parent-portal
 * person, since those are created not-enrolled.
 */

/*
 * NOT Firestore's `in` limit, which is 30. The ceiling here is the SECURITY RULES' document-access
 * budget, and it is the `in` FORM that spends it: such a read is charged as one lookup per id
 * against the request's twenty calls, and `canReadUsers` and `canReadClient` both spend one on
 * `getUser()` before reading anything. Twenty ids are refused however readable each one is alone.
 * A plain `where()` is not bounded this way — `loadClinicPeople` returns far more than twenty —
 * so this is a constraint on reading BY ID, not on how much may come back.
 *
 * At 30 this read failed for any client with a large enough team, and failed as
 * "Missing or insufficient permissions" — which sends you looking at the data, where there is
 * nothing to find. Ten leaves room for another get() in either rule. See
 * `firestore/usersReadBudget.rules.test.js`, which pins the boundary.
 */
const MAX_IN_VALUES = 10;

/**
 * The stored value for a record that does not consume a client seat.
 *
 * Lives here and travels no further. It answers a licensing question rather than a domain one, so
 * the layers above must never see it: a composable that had to pass this in would be carrying a
 * storage detail it has no business knowing.
 */
const NON_CLIENT_SEAT = 'staff';

const chunk = (values, size) => {
  return values.reduce((chunks, value, index) => {
    if (index % size === 0) {
      chunks.push([]);
    }
    chunks[chunks.length - 1].push(value);

    return chunks;
  }, []);
}; // chunk()

/**
 * Reads documents by id, in batches the `in` filter will accept.
 *
 * `also` carries whatever else every batch has to satisfy, which is how the clinic predicate reaches
 * a query built around ids. It applies to each chunk rather than to the result, because a filter the
 * database never saw is not a scope.
 */
async function readByIds(db, ids, also = []) {
  const batches = await Promise.all(
    chunk([...new Set(ids)].filter(Boolean), MAX_IN_VALUES)
      .map((batch) => db.readAll([...also, [documentId(), 'in', batch]])),
  );

  return batches.flat();
} // readByIds()

/**
 * One person, projected. Used for the client themselves and for a policyholder already on a
 * coverage, both of which have to render before any search happens.
 *
 * @param {string} personId
 * @returns {Promise<import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics|null>}
 */
export async function loadPerson(personId) {
  const document = await new ClientsDB().read(personId);

  return document ? toPolicyholderDemographics(document) : null;
} // loadPerson()

/**
 * Several people at once, projected, for the rows of a list that already knows whose ids it needs.
 *
 * ONE QUERY RATHER THAN ONE PER ROW. A client's coverages usually name the same one or two people,
 * and the ids are all known before the first read, so there is nothing to discover by asking
 * separately. `readByIds` chunks to whatever the `in` filter accepts.
 *
 * SCOPED BY CLINIC, not only by id. These ids come off stored coverages rather than out of a picker,
 * so this is the first place in the chain that can state where those people are expected to be. An
 * id belonging to another clinic simply does not come back, which the caller renders as an
 * unreadable policyholder rather than as somebody else's name.
 *
 * @param {Object} scope
 * @param {string[]} scope.personIds
 * @param {string} scope.clinicId
 * @returns {Promise<import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics[]>}
 */
export async function loadPeople({ personIds, clinicId }) {
  if (personIds.length === 0) {
    return [];
  }

  const documents = await readByIds(new ClientsDB(), personIds, [['clinic', '==', clinicId]]);

  return documents.map(toPolicyholderDemographics);
} // loadPeople()

/**
 * Everyone in the clinic who could hold a policy, read once for in-memory matching.
 *
 * @param {string} clinicId
 * @returns {Promise<import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics[]>}
 */
export async function loadClinicPeople(clinicId) {
  const documents = await new ClientsDB().readAll([
    ['clinic', '==', clinicId],
    ['type', '==', NON_CLIENT_SEAT],
  ]);

  return documents.map(toPolicyholderDemographics);
} // loadClinicPeople()

/**
 * The people already reachable from this client, as person ids.
 *
 * ACCESS, NOT RELATIONSHIP. This is who can see the client, which is a ranking hint and nothing
 * more. It must never be mapped into the domain as kinship, and the group it feeds must never be
 * labelled as one: a coordinator with access is not family, and a grandparent without a login is
 * family and will not appear here at all.
 *
 * Three reads rather than a walk per person: the access rows, then the users they name, then the
 * person records those users point at.
 *
 * @param {string} clientId
 * @returns {Promise<string[]>}
 */
export async function loadConnectedPersonIds(clientId) {
  const access = await new UsersClientsDB().readAll([['client', '==', clientId]]);
  const uids = access.map(({ user }) => user).filter(Boolean);

  if (uids.length === 0) {
    return [];
  }

  const users = await readByIds(new UsersDB(), uids);

  return users.map(({ staffMember }) => staffMember).filter(Boolean);
} // loadConnectedPersonIds()

/**
 * Corrects or completes what we know about somebody already on file.
 *
 * Also through Identity & Tenancy, and for a sharper reason than the create: an edit is where a
 * caller would otherwise be tempted to write the one field it happens to need, and the allowlist
 * exists because that document belongs to several owners at once.
 *
 * @param {string} personId
 * @param {Object} profile - identity and demographics; anything else is dropped server-side
 * @returns {Promise<void>}
 */
export async function updatePerson(personId, profile) {
  await callFirebaseFunction('updatePerson', { personId, profile });
} // updatePerson()

/**
 * Records a new person in the clinic and returns their id.
 *
 * Goes through Identity & Tenancy rather than writing the collection, so the field allowlist and the
 * seat discriminator are enforced somewhere a browser cannot skip.
 *
 * @param {string} clinicId
 * @param {Object} profile - identity and demographics; anything else is dropped server-side
 * @returns {Promise<string>} the new person id
 */
export async function createPerson(clinicId, profile) {
  // The callable's payload is under `data`; callFirebaseFunction hands back the raw result. Reading
  // the id off the wrapper yields undefined, and nothing downstream complains: the person is created,
  // the caller gets no id, and the field it was meant to fill stays empty.
  const { data } = await callFirebaseFunction('createPerson', { clinicId, profile });

  return data.personId;
} // createPerson()
